import { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import { Col, Container, Row, Button, FormGroup, Modal,
  ModalHeader,
  ModalBody,
  ModalFooter, Spinner } from "reactstrap";
import { ReactstrapInput, ReactstrapSelect } from "reactstrap-formik";

import * as Yup from "yup";

import config from "../../store/config";
import FormHelpers from "../../helpers/FormHelpers";
import InfoTooltip from "../UI/Progress/InfoTooltip";
import ScrollToFieldError from "./Validation/ScrollToFieldError";

import { configuratorActions } from "../../store/configurator-slice";
import { sendCartData } from "../../store/configurator-actions";
if (typeof window.SMA_DEFAULT_CONFIG !== "undefined") {
  config.settings = window.SMA_DEFAULT_CONFIG;
}

function ReturnForm(props) {
  const dispatch = useDispatch();

  const formRef = useRef();

  // get current request state
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );
  
  const addingToCart = useSelector((state) => state.configurator.AddingToCart);
  useEffect(() => {
    if (addingToCart) {
      let currentCart = { ...requestDetails };
      dispatch(sendCartData(currentCart));
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [dispatch, addingToCart]);const requestDetails = currentRequest.DonorRequest;



  const isLoggedIn = currentRequest.DonorIsLoggedIn;
  let useAddressValue = requestDetails.UseAddress;
  if (!useAddressValue) {
    useAddressValue = "billing";
  }

  // get title list
  const titles = currentRequest.Lists.DonorTitles;
  const titlesArray = titles.map((item) => {
    return item.Value;
  });

  // get country list
  const countries = currentRequest.Lists.Countries;
  const countriesArray = countries.map((item) => {
    return item.Value;
  });

  // get state list
  const states = currentRequest.Lists.States;
  const statesArray = states.map((item) => {
    return item.Value;
  });

  // get province list
  const provinces = currentRequest.Lists.Provinces;
  const provincesArray = provinces.map((item) => {
    return item.Value;
  });

  // init form validation schema
  let formSchema = Yup.object().shape({
    firstName: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),
    lastName: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),
    address1: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),
    country: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),
    city: Yup.string().when("useAddress", (useAddress) => {
      if (useAddress === 'custom') return Yup.string().required("Required");
    }),

    state: Yup.string().when(
      ["country", "useAddress"],
      (country, useAddress) => {
        if (useAddress === 'custom' && country === "USA")
          return Yup.string().required("Required");
      }
    ),

    province: Yup.string().when(
      ["country", "useAddress"],
      (country, useAddress) => {
        if (useAddress === 'custom' && country === "Canada")
          return Yup.string().required("Required");
      }
    ),

    postalCode: Yup.string().when(
      ["country", "useAddress"],
      (country, useAddress) => {
        if (
          useAddress === 'custom' &&
          (country === "USA" || country === "Canada")
        ) {
          if(country === "USA") {
            return Yup.string().required("Required").matches(/^[0-9]{5}(?:-[0-9]{4})?$/, "Must be a valid zip code");
          } else {
            return Yup.string().required("Required").matches(/^(?!.*[DFIOQU])[A-VXY][0-9][A-Z][ -]?[0-9][A-Z][0-9]$/, "Must be a valid postal code");
          }
        }
      }
    ),
  });

  const useAddressClickHandler = (e) => {
    dispatch(configuratorActions.setUseAddressValue(e.target.value));
  };

  
  
  
  

  const noAddressClickHandler = (values) => {
    dispatch(configuratorActions.setReturn(values));
  };

  const previousClickHandler = (values) => {
    dispatch(configuratorActions.setFormStep("Recipient"));
    FormHelpers.scrollToTop();
  };

  const inputFocusHandler = (e) => {
    
  };

  const inputBlurHandler = (e) => {
    
  };

  const addressUpdateHandler = (e) => {
    let values = {...formRef.current.values};
    values[e.target.name] = e.target.value;

    let envelopeValues = {...values};

    if(values.country === 'USA') {
      envelopeValues.state = FormHelpers.getStateProvinceCode(states, values.state);
    } else if(values.country === 'Canada') {
      envelopeValues.province = FormHelpers.getStateProvinceCode(provinces, values.province);
    }

  };

  // START ADDRESS VERIFICATION
  const [sessionToken, setSessionToken] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [nextButtonLabel, setNextButtonLabel] = useState("Next");
  const [addressVerifyMessage, setAddressVerifyMessage] = useState("");
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(-1);
  const [isSuggestionListFocused, setIsSuggestionListFocused] = useState(false);
  const [useAddressAsEntered, setUseAddressAsEntered] = useState(false);
  const [verifiedAddressValues, setVerifiedAddressValues] = useState(null);
  const [showAddressCorrectionModal, setShowAddressCorrectionModal] = useState(false);
  const [addressCorrectionModalMsg, setAddressCorrectionModalMsg] = useState(null);
  const toggleAddressCorrectionModal = (e) => {
    setShowAddressCorrectionModal(!showAddressCorrectionModal);
  };

  // Refs to Google services
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  // Get a reference to the input element after render
  useEffect(() => {
    // Find the input element inside the Field component
    const inputElement = document.getElementById('address1');
    if (inputElement) {
      inputRef.current = inputElement;
    }
  }, []);

  // Initialize Google services once the script has loaded
  useEffect(() => {
    if (window.google) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();

      // PlacesService needs a DOM node or a map, so we can create an offscreen <div>
      const offscreenDiv = document.createElement("div");
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        offscreenDiv
      );
    }
  }, []);

  const generateNewSessionToken = () => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
    }
  };

  useEffect(() => {
    generateNewSessionToken();

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const address1UpdateHandler = (e) => {
    const { value } = e.target;
    setInputValue(value);
    formRef.current.setFieldValue("address1", value);

    // If empty, reset suggestions
    if (!value) {
      setSuggestions([]);
      return;
    }

    // If we have an AutocompleteService, call getPlacePredictions with the session token
    if (autocompleteServiceRef.current && sessionToken) {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: value,
          sessionToken: sessionToken, // Use the session token
          // optional: componentRestrictions: { country: ['us','ca'] }
        },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    }

    addressUpdateHandler(e);

    // Reset focused index when suggestions change
    setFocusedSuggestionIndex(-1);
  };

  const formatAddressString = (addressString) => {
    const replacements = [
      { pattern: /street/gi, replacement: 'St' },
      { pattern: /drive/gi, replacement: 'Dr' },
      { pattern: /avenue/gi, replacement: 'Ave' },
      { pattern: /boulevard/gi, replacement: 'Blvd' },
      { pattern: /circle/gi, replacement: 'Cir' },
      { pattern: /court/gi, replacement: 'Ct' },
      { pattern: /square/gi, replacement: 'Sq' },
      { pattern: /place/gi, replacement: 'Pl' },
      { pattern: /road/gi, replacement: 'Rd' },
      { pattern: /lane/gi, replacement: 'Ln' },
      { pattern: /trak/gi, replacement: 'Trk' }
    ];
  
    let formattedAddress = addressString;
    replacements.forEach(({ pattern, replacement }) => {
      formattedAddress = formattedAddress.replace(pattern, replacement);
    });
  
    return formattedAddress;
  };

  // Called when user clicks on one of the suggestions
  const handleSuggestionClick = (suggestion) => {

    setInputValue(formatAddressString(suggestion.description));
    setSelectedPlaceId(suggestion.place_id);
    setSuggestions([]);

    // Immediately fetch full place details to populate city, state, etc.
    if (placesServiceRef.current && suggestion.place_id) {
      placesServiceRef.current.getDetails(
        { placeId: suggestion.place_id },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            // Parse address components
            let address1 = "";
            let city = "";
            let stateOrProvince = "";
            let postalCode = "";
            let country = "";
            
            //console.log(place);

            if (place.address_components) {
              place.address_components.forEach((component) => {
                const types = component.types;
                if (types.includes("street_number")) {
                  address1 = component.long_name;
                }
                if (types.includes("route")) {
                  address1 += " " + component.long_name;
                }
                if (types.includes("locality")) {
                  city = component.long_name;
                }
                if (types.includes("administrative_area_level_1")) {
                  stateOrProvince = component.long_name;
                }
                if (types.includes("country")) {
                  country = component.long_name;
                }
                if (types.includes("postal_code")) {
                  postalCode = component.long_name;
                }
              });
            }

            
            if(country === "United States") {
              country = "USA";
            }

            // Update relevant Formik fields
            formRef.current.setFieldValue("address1", address1);
            formRef.current.setFieldValue("city", city);
            formRef.current.setFieldValue("country", country);
            if(country === "USA") {
              formRef.current.setFieldValue("state", stateOrProvince);
            } else if(country === "Canada") {
              formRef.current.setFieldValue("province", stateOrProvince);
            } else {
              formRef.current.setFieldValue("city", city + ", " + stateOrProvince);
            }
            formRef.current.setFieldValue("postalCode", postalCode);

            let values = { 
              firstName: formRef.current.values.firstName,
              lastName: formRef.current.values.lastName,
              address1: address1,
              address2: '',
              city: city,
              state: stateOrProvince,
              province: stateOrProvince,
              country: country,
              postalCode: postalCode
            };

            let envelopeValues = { ...values };

            if (values.country === "USA") {
              envelopeValues.state = FormHelpers.getStateProvinceCode(
                states,
                values.state
              );
            } else if (values.country === "Canada") {
              envelopeValues.province = FormHelpers.getStateProvinceCode(
                provinces,
                values.province
              );
            }

          }
        }
      );
    }

    // Reset state and focus input after selection
    setSuggestions([]);
    setFocusedSuggestionIndex(-1);
    setIsSuggestionListFocused(false);
    inputRef.current?.focus();  
  };

  // Handle keyboard navigation from the input field
  const handleInputKeyDown = (e) => {
    if (!suggestions.length) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedSuggestionIndex(0);
        setIsSuggestionListFocused(true);
        // Focus the first suggestion item
        setTimeout(() => {
          const firstItem = suggestionsRef.current?.querySelector('li');
          if (firstItem) firstItem.focus();
        }, 10);
        break;
      case 'Tab':
        if (suggestions.length && !isSuggestionListFocused) {
          e.preventDefault();
          setFocusedSuggestionIndex(0);
          setIsSuggestionListFocused(true);
          // Focus the first suggestion item
          setTimeout(() => {
            const firstItem = suggestionsRef.current?.querySelector('li');
            if (firstItem) firstItem.focus();
          }, 10);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSuggestions([]);
        break;
      default:
        break;
    }
  };

  // Handle suggestion item keyboard navigation
  const handleSuggestionKeyDown = (e, suggestion, index) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (index < suggestions.length - 1) {
          setFocusedSuggestionIndex(index + 1);
          const items = suggestionsRef.current?.querySelectorAll('li');
          if (items && items[index + 1]) items[index + 1].focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (index > 0) {
          setFocusedSuggestionIndex(index - 1);
          const items = suggestionsRef.current?.querySelectorAll('li');
          if (items && items[index - 1]) items[index - 1].focus();
        } else {
          // Return focus to input when at the top of the list
          setFocusedSuggestionIndex(-1);
          setIsSuggestionListFocused(false);
          inputRef.current?.focus();
        }
        break;
      case 'Enter':
        e.preventDefault();
        handleSuggestionClick(suggestion);
        break;
      case 'Escape':
        e.preventDefault();
        setSuggestions([]);
        setFocusedSuggestionIndex(-1);
        setIsSuggestionListFocused(false);
        inputRef.current?.focus();
        break;
      case 'Tab':
        // Allow normal tab behavior to move to next form element
        setIsSuggestionListFocused(false);
        setSuggestions([]);
        break;
      default:
        break;
    }
  };

  const handleClickOutside = (event) => {
    if (
      suggestionsRef.current &&
      !suggestionsRef.current.contains(event.target)
    ) {
      setSuggestions([]); // Hide the suggestions box
    }
  };
  document.addEventListener("mousedown", handleClickOutside);

  async function verifyAddressOnServer(values) {
    // Build a request body that matches the C# AddressModel
    // Notice the naming matches Address1, City, State, etc.
    const body = {
      sessionToken: sessionToken.gw,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      state: values.state,
      province: values.province,
      country: values.country,
      zip: values.postalCode,
    };

    try {

      const response = await fetch(config.settings.apiEndpoint + "api/requests/VerifyAddress", {
        method: "POST",
        credentials: "include",
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': window.AntiForgeryToken
          },
        body: JSON.stringify(body),
        // credentials: "include" if you need cookies for auth
      });

      if (!response.ok) {
        // HTTP error (e.g. 400 Bad Request)
        const errorData = await response.json();
        return { success: false, error: errorData.error };
      }

      const data = await response.json();
      // data should look like { success: true, standardizedAddress: {...} } or { success: false, error: ... }

      if (data.success) {
        return { success: true, standardizedAddress: data.standardizedAddress };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error("Server verification error:", err);
      return { success: false, error: err.message };
    }
  }

  if(useAddressValue !== "custom") {
    formSchema = Yup.object().shape();
  }

  // Handler for "Update Address" button
  const handleUpdateAddress = () => {
    // Apply verified address to form fields
    if (verifiedAddressValues) {
      // Update form fields with verified values
      Object.entries(verifiedAddressValues).forEach(([field, value]) => {
        if(field != "address1" && field != "address2") {
          formRef.current.setFieldValue(field, value);
        }
      });
      
      // Submit the  form with verified values
      dispatch(configuratorActions.setReturn(verifiedAddressValues));
      updateEnvelopeDisplay(verifiedAddressValues);
      FormHelpers.scrollToTop();
    }
    
    setShowAddressCorrectionModal(false);
  };

  // Handler for "Please use my address as entered"
  const useAddressAsEnteredHandler = () => {
    setUseAddressAsEntered(true);
    setShowAddressCorrectionModal(false);
    
    // Submit with current form values
    const currentValues = formRef.current.values;
    dispatch(configuratorActions.setReturn(currentValues));
    updateEnvelopeDisplay(currentValues);
    FormHelpers.scrollToTop();
  };

  const updateEnvelopeDisplay = (values) => {

    let envelopeValues = { ...values };

    if (values.country === "USA") {
      envelopeValues.state = FormHelpers.getStateProvinceCode(
        states,
        values.state
      );
    } else if (values.country === "Canada") {
      envelopeValues.province = FormHelpers.getStateProvinceCode(
        provinces,
        values.province
      );
    }

  };

  const formSubmitHandler = async (values) => {
    setAddressVerifyMessage("");
    
    if(values.firstName == requestDetails.RecipientFirstName && values.lastName == requestDetails.RecipientLastName && values.address1 == requestDetails.RecipientAddress1) {
      setAddressVerifyMessage("Your return address information matches your recipient address information. Please make sure you are using different recipient and return addresses.");
      return;
    }

    dispatch(configuratorActions.setReturn(values));
    FormHelpers.scrollToTop();
    return;
    
    // If we already have verified address values or user chose to use address as entered,
    // skip verification and submit directly
    if (verifiedAddressValues || useAddressAsEntered) {
      const finalValues = verifiedAddressValues || values;
      dispatch(configuratorActions.setReturn(finalValues));
      FormHelpers.scrollToTop();
      return;
    }
    
    setNextButtonLabel("Please wait...");
    setShowAddressCorrectionModal(false);
    setAddressCorrectionModalMsg("");
          
    if( (useAddressValue == "custom") ) {
      const verifyResult = await verifyAddressOnServer(values);
  
      if (!verifyResult.success) {
        setNextButtonLabel("Next");
        setAddressVerifyMessage("We are unable to verify the entered address. Please confirm the correct address details and try again.");
        return;
      } else {
        // Update relevant Formik fields
        const verifiedAddress = verifyResult.standardizedAddress;
        const currentFormValues = formRef.current.values;
  
        // Create the verified values object
        let verifiedValues = { 
          firstName: currentFormValues.firstName,
          lastName: currentFormValues.lastName,
          title: currentFormValues.title,
          useAddress: currentFormValues.useAddress,
          address1: currentFormValues.address1,
          address2: currentFormValues.address2,
          city: verifiedAddress.locality,
          state: verifiedAddress.administrativeArea,
          province: verifiedAddress.administrativeArea,
          country: verifiedAddress.regionCode,
          postalCode: verifiedAddress.postalCode.split('-')[0]
        };
  
        // Store the verified values for later use
        setVerifiedAddressValues(verifiedValues);
  
        let envelopeValues = { ...verifiedValues };
  
        if (verifiedValues.country === "USA") {
          envelopeValues.state = FormHelpers.getStateProvinceCode(
            states,
            verifiedValues.state
          );
        } else if (verifiedValues.country === "Canada") {
          envelopeValues.province = FormHelpers.getStateProvinceCode(
            provinces,
            verifiedValues.province
          );
        }
  
        let addressMatchMsg = "";
        // if(FormHelpers.normalizeAddressForComparison(currentFormValues.address1) != FormHelpers.normalizeAddressForComparison(verifiedValues.address1)) {
        //   addressMatchMsg += "Address 1 will be changed to: <strong style=\"font-weight: 700\">" + verifiedValues.address1 + "</strong><br />";
        // }
        // if(FormHelpers.normalizeAddressForComparison(currentFormValues.address2) != FormHelpers.normalizeAddressForComparison(verifiedValues.address2)) {
        //   addressMatchMsg += "Address 2 will be changed to: <strong style=\"font-weight: 700\">" + verifiedValues.address2 + "</strong><br />";
        // }
        if(currentFormValues.city != verifiedValues.city) {
          addressMatchMsg += "City will be changed to: <strong style=\"font-weight: 700\">" + verifiedValues.city + "</strong><br />";
        }
        if(currentFormValues.state && currentFormValues.state != verifiedValues.state) {
          addressMatchMsg += "State will be changed to <strong style=\"font-weight: 700\">" + verifiedValues.state + "</strong><br />";
        }
        if(currentFormValues.province && currentFormValues.province != verifiedValues.province) {
          addressMatchMsg += "Province will be changed to <strong style=\"font-weight: 700\">" + verifiedValues.province + "</strong><br />";
        }
        if(currentFormValues.country != verifiedValues.country) {
          addressMatchMsg += "Country will be changed to <strong style=\"font-weight: 700\">" + verifiedValues.country + "</strong><br />";
        }
        if(currentFormValues.postalCode != verifiedValues.postalCode) {
          addressMatchMsg += "Postal code will be changed to <strong style=\"font-weight: 700\">" + verifiedValues.postalCode + "</strong><br />";
        }
  
        if(addressMatchMsg) {
          setShowAddressCorrectionModal(true);
          setAddressCorrectionModalMsg("To improve shipping accuracy, a suggested address update was found based on your entered address. Please see below for details and confirmation: <br /><br />" + addressMatchMsg);
          setNextButtonLabel("Next");
          return;
        } else {
          // If no changes needed, submit with verified values
          dispatch(configuratorActions.setReturn(verifiedValues));
          FormHelpers.scrollToTop();
          return;
        }
      }
    }
  
    // For billing address or if no verification needed
    FormHelpers.scrollToTop();
  };
  // END ADDRESS VERIFICATION

  return (
    <div className="cfg-input-form cfg-return" data-step="return">
      <Formik
        initialValues={{
          useAddress: useAddressValue,
          title: requestDetails.ReturnTitle,
          firstName: requestDetails.ReturnFirstName,
          lastName: requestDetails.ReturnLastName,
          address1: requestDetails.ReturnAddress1,
          address2: requestDetails.ReturnAddress2,
          country: (requestDetails.ReturnCountry) ? requestDetails.ReturnCountry : 'USA',
          state: requestDetails.ReturnStateProvince,
          province: requestDetails.ReturnStateProvince,
          city: requestDetails.ReturnCity,
          postalCode: requestDetails.ReturnZipPostalCode,
        }}
        validationSchema={formSchema}
        onSubmit={formSubmitHandler}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="text-start">
            <ScrollToFieldError />
            <Container className="cfg-form-section">
              <Row>
                <Col xs="12">
                  <InfoTooltip
                    id="returnHelp"
                    title="Help"
                    text="Fill in your name and address unless you are sending the card on behalf of someone else.  In that case type the name and address you want printed on the envelope."
                  />
                  <FormGroup>

                        <div className="form-check">
                          <label>
                            <Field
                              type="radio"
                              name="useAddress"
                              value="billing"
                              onClick={useAddressClickHandler}
                            />
                            Use my billing address
                          </label>
                        </div>

                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="useAddress"
                          value="custom"
                          onClick={useAddressClickHandler}
                        />
                        Use the name and address below
                      </label>
                    </div>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="useAddress"
                          value="none"
                          onClick={useAddressClickHandler}
                        />
                        No Return Address
                      </label>
                    </div>
                    {errors.useAddress && touched.useAddress && (
                      <div className="invalid-feedback">
                        {errors.useAddress}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {useAddressValue !== "none" && useAddressValue !== "billing" && (
                <div>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label htmlFor="title">Title:</label>
                      <Field
                        name="title"
                        component={ReactstrapSelect}
                        onChange={(e) => {
                          handleChange(e);
                          addressUpdateHandler(e);
                        }}
                        inputprops={{
                          name: "title",
                          id: "title",
                          options: titlesArray,
                          defaultOption: "Select...",
                        }}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                        <label className="required" htmlFor="firstName">
                          First Name:
                        </label>
                        <Field
                          name="firstName"
                          id="firstName"
                          maxLength="30"
                          component={ReactstrapInput}
                          onKeyUp={addressUpdateHandler}
                          onFocus={inputFocusHandler}
                          onBlur={(e) => {
                            handleBlur(e);
                            inputBlurHandler(e);
                          }}
                        />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label className="required" htmlFor="lastName">
                        Last Name:
                      </label>
                      <Field
                        name="lastName"
                        id="lastName"
                        maxLength="30"
                        component={ReactstrapInput}
                        onKeyUp={addressUpdateHandler}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label className="required" htmlFor="address1">
                        Address 1:
                      </label>
                      <Field
                      name="address1"
                      id="address1"
                      maxLength="50"
                      autoComplete="off"
                      component={ReactstrapInput}
                      onKeyUp={(e) => {
                        address1UpdateHandler(e);
                      }}
                      onKeyDown={handleInputKeyDown}
                      onFocus={inputFocusHandler}
                      onBlur={(e) => {
                        if (!isSuggestionListFocused) {
                          setTimeout(() => {
                            handleBlur(e);
                            inputBlurHandler(e);
                          }, 100);
                        }
                      }}
                    />
                    {suggestions.length > 0 && (
                      <ul
                        ref={suggestionsRef}
                        role="listbox"
                        tabIndex={-1}
                        aria-label="Address suggestions"
                        style={{
                          position: "absolute",
                          background: "#fff",
                          border: "1px solid #ccc",
                          margin: 0,
                          padding: 0,
                          listStyle: "none",
                          width: "300px",
                          maxHeight: "150px",
                          overflowY: "auto",
                          zIndex: 999,
                        }}
                        onMouseDown={(e) => {
                          // Prevent blur event on input when clicking the list
                          e.preventDefault();
                        }}
                      >
                        {suggestions.map((suggestion, index) => (
                          <li
                            key={suggestion.place_id}
                            id={`suggestion-${suggestion.place_id}`}
                            role="option"
                            aria-selected={focusedSuggestionIndex === index}
                            tabIndex={0}
                            style={{
                              padding: "8px",
                              cursor: "pointer",
                              backgroundColor: focusedSuggestionIndex === index ? "#e9ecef" : "transparent",
                              outline: "none", // Remove default focus outline
                            }}
                            onClick={() => handleSuggestionClick(suggestion)}
                            onKeyDown={(e) => handleSuggestionKeyDown(e, suggestion, index)}
                            onFocus={() => {
                              setFocusedSuggestionIndex(index);
                              setIsSuggestionListFocused(true);
                            }}
                            onBlur={() => {
                              // Only reset if we're not focusing another suggestion
                              setTimeout(() => {
                                if (!suggestionsRef.current?.contains(document.activeElement)) {
                                  setIsSuggestionListFocused(false);
                                  setSuggestions([]);
                                }
                              }, 100);
                            }}
                          >
                            {suggestion.description}
                          </li>
                        ))}
                      </ul>
                    )}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label htmlFor="address2">Address 2:</label>
                      <Field
                        name="address2"
                        id="address2"
                        maxLength="50"
                        component={ReactstrapInput}
                        onKeyUp={addressUpdateHandler}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="12">
                      <label className="required" htmlFor="country">
                        Country:
                      </label>
                      <Field
                        name="country"
                        component={ReactstrapSelect}
                        inputprops={{
                          name: "country",
                          id: "country",
                          options: countriesArray,
                          defaultOption: "Select...",
                        }}
                        onChange={(e) => {
                          handleChange(e);
                          addressUpdateHandler(e);
                        }}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                  {values.country === "USA" && (
                    <Row className="mb-2">
                      <Col xs="12">
                        <label
                          className={values.country === "USA" ? "required" : ""}
                          htmlFor="state"
                        >
                          State:
                        </label>
                        <Field
                          name="state"
                          component={ReactstrapSelect}
                          inputprops={{
                            name: "state",
                            id: "state",
                            options: statesArray,
                            defaultOption: "Select...",
                          }}
                          onChange={(e) => {
                            handleChange(e);
                            addressUpdateHandler(e);
                          }}
                          onFocus={inputFocusHandler}
                          onBlur={(e) => {
                            handleBlur(e);
                            inputBlurHandler(e);
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                  {values.country === "Canada" && (
                    <Row className="mb-2">
                      <Col xs="12">
                        <label
                          className={
                            values.country === "Canada" ? "required" : ""
                          }
                          htmlFor="province"
                        >
                          Province:
                        </label>
                        <Field
                          name="province"
                          component={ReactstrapSelect}
                          inputprops={{
                            name: "province",
                            id: "province",
                            options: provincesArray,
                            defaultOption: "Select...",
                          }}
                          onChange={(e) => {
                            handleChange(e);
                            addressUpdateHandler(e);
                          }}
                          onFocus={inputFocusHandler}
                          onBlur={(e) => {
                            handleBlur(e);
                            inputBlurHandler(e);
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className="mb-2">
                    <Col xs="6">
                      <label className="required" htmlFor="city">
                        City:
                      </label>
                      <Field
                        name="city"
                        id="city"
                        maxLength="50"
                        component={ReactstrapInput}
                        onKeyUp={addressUpdateHandler}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                    <Col xs="6">
                      <label
                        className={
                          values.country === "USA" ||
                          values.country === "Canada"
                            ? "required"
                            : ""
                        }
                        htmlFor="postalCode"
                      >
                        Zip/Postal Code:
                      </label>
                      <Field
                        name="postalCode"
                        id="postalCode"
                        maxLength="10"
                        component={ReactstrapInput}
                        onKeyUp={addressUpdateHandler}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler(e);
                        }}
                      />
                    </Col>
                  </Row>
                </div>
              )}
              {/* <DebugOutput
                errors={errors}
                values={FormHelpers.getValues(values)}
              /> */}
            </Container>
            <Button
              type="button"
              size="sm"
              color="primary"
              onClick={previousClickHandler}
              className="btn-secondary"
            >
              Previous
            </Button>{" "}
            <Button
              type="submit"
              size="sm"
              color="primary"
              className="float-end"
              disabled={nextButtonLabel != "Next"}
            >
              {nextButtonLabel}
            </Button>
            {addressVerifyMessage != "" && <div className="alert alert-danger" role="alert">{addressVerifyMessage}</div>}

            <Modal
              isOpen={showAddressCorrectionModal}
              toggle={toggleAddressCorrectionModal}
              size="md"
              backdrop="static"
            >
              <ModalHeader toggle={toggleAddressCorrectionModal} close={() => null}>
                Address Update
              </ModalHeader>
              <ModalBody>
                <Container>
                  <Row>
                    <Col>
                      <p
                        dangerouslySetInnerHTML={{ __html: addressCorrectionModalMsg }}
                        style={{ fontSize: '16px' }}
                      />
                    </Col>
                  </Row>
                </Container>
              </ModalBody>
              <ModalFooter style={{ flexDirection: 'column' }}>
                <Button
                  color="primary"
                  onClick={handleUpdateAddress}
                >
                  Update Address
                </Button>
                <a 
                  onClick={useAddressAsEnteredHandler} 
                  style={{ 
                    display: 'block', 
                    marginTop: '10px', 
                    color: '#337ab7', 
                    textDecoration: 'underline', 
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Please use my address as entered
                </a>
              </ModalFooter>
            </Modal>
          </Form>
        )}
      </Formik>

      {nextButtonLabel != "Next" && <Spinner color="primary">Verifying Address...</Spinner>}

    </div>
  );
}

export default ReturnForm;
